/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlaceIcon from "@mui/icons-material/Place";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { ActivityTimer } from "components/common/ActivityTimer";
import { LoadedContent } from "components/common/loading/loadedContent";
import { DateStepper } from "components/DateStepper";
import {
  EventFilterProvider,
  useEventFilterMatcher,
} from "components/manualReview/contexts/EventFilterContext";
import { thinTimelineTicks } from "components/manualReview/DayTimeline/thinTimelineTicks";
import {
  ManualReviewEventProvider,
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "components/manualReview/contexts/ManualReviewEventContext";
import { useExcludedPeriods } from "components/manualReview/contexts/ExcludedPeriodsContext";
import { EventFilterBar } from "components/manualReview/filters/EventFilterBar";
import { useRailcarTimelineTicks } from "components/manualReview/hooks/useRailcarTimelineTicks";
import { useMaterialExclusion } from "components/manualReview/contexts/MaterialExclusionContext";
import { useTimingExclusion } from "components/manualReview/contexts/TimingExclusionContext";
import { MassDataProvider } from "components/manualReview/contexts/MassDataContext";
import { TimelineExclusionProviders } from "components/manualReview/TimelineExclusionProviders";
import { HotkeySafeCheckbox } from "components/manualReview/components/HotkeySafeCheckbox";
import { useVerifyPeriods } from "components/manualReview/hooks/useVerifyPeriods";
import { HotkeyProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { VideoControlProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useActivityTimer } from "hooks/useActivityTimer";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useQueryParamState } from "hooks/useQueryParamState";
import { requestedReviewReasons } from "components/manualReview/filters/eventFilters";
import { reviewReasonListSerialiser } from "hooks/serialisers/reviewReasonList";
import { loadedEndpoint } from "models/loaded";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  EventsForReview,
  ReviewDayOverview,
  ReviewPeriodData,
  LocationExceptionalStatusRead,
  useGetLocationsLoadingTimelineQuery,
  useGetReviewDayOverviewQuery,
  useGetSiteReviewPeriodsQuery,
  useListLocationExceptionalStatusesQuery,
} from "src/store/api/generatedApi";
import { AssignedReviewIndicator } from "./AssignedReviewIndicator";
import { VerificationPreviewProvider } from "./VerificationPreviewDialog";
import { LinkedPeriodBringInProvider } from "./LinkedPeriodBringInDialog";
import { ExceptionDraftProvider } from "./contexts/ExceptionDraftContext";
import { ExceptionDraftDialog } from "./dayExceptions/ExceptionDraftDialog";
import { DayTimeline } from "./DayTimeline/DayTimeline";
import {
  LoadingInterval,
  locationLoadingIntervals,
} from "./DayTimeline/locationLoadingIntervals";
import {
  hasExpandableLane,
  LocationTimelineStack,
} from "./DayTimeline/LocationTimelineStack";
import {
  DEFAULT_WINDOW_MINUTES,
  useManualReviewNavigation,
} from "./hooks/useManualReviewNavigation";
import { useReviewMonthIndicators } from "./hooks/useReviewMonthIndicators";
import { useReviewEventsWithExclusions } from "./hooks/useReviewEventsWithExclusions";
import { TimelineExclusionReport } from "./hooks/useTimelineExclusions";
import {
  defaultExclusionPeriodIds,
  labellerExclusionDecisions,
} from "./utils/timelineExclusions";
import { ManualReviewLegend } from "./ManualReviewLegend";
import { ManualReviewSurface } from "./ManualReviewSurface";
import { filterReviewPeriods } from "./utils/formatters";
import { dayjsSerializer, parseFocusTimestampParam } from "./utils/timeUtils";
import { VideoPlayerGroupProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoPlayerGroupContext";

const EMPTY_EXCEPTIONAL_STATUSES: LocationExceptionalStatusRead[] = [];

export const ManualReviewLocationPicker = () => {
  const { t } = useTenantTranslation();

  const { locationNames: locations, isLoading } = useLabellingLocations();

  return (
    <AccountPageLayout title={t("manualReview")}>
      <Stack spacing={3} py={4}>
        <Typography variant="h5">{t("selectLocation")}</Typography>
        <Typography variant="body1" color="text.secondary">
          {t("selectLocationToReview")}
        </Typography>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : locations.length === 0 ? (
          <Alert severity="info">{t("noLocationsConfigured")}</Alert>
        ) : (
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {locations.map((location) => (
              <Card key={location} sx={{ minWidth: 200 }}>
                <CardActionArea component={Link} to={location} sx={{ p: 2 }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <PlaceIcon color="primary" sx={{ fontSize: 32 }} />
                      <Typography variant="h6">{location}</Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </AccountPageLayout>
  );
};

const ManualReviewDateStepper = ({
  location,
  selectedDate,
  setSelectedDate,
}: {
  location: string | undefined;
  selectedDate: Dayjs;
  setSelectedDate: (date: Dayjs) => void;
}) => {
  const [viewedMonth, setViewedMonth] = useState<Dayjs>(selectedDate);
  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- follow selectedDate into its month when the user picks a new date
    setViewedMonth(selectedDate);
  }, [selectedDate]);

  const { indicators, isFetching } = useReviewMonthIndicators({
    location,
    month: viewedMonth,
  });
  return (
    <DateStepper
      date={selectedDate}
      setDate={setSelectedDate}
      dayIndicators={indicators}
      indicatorsLoading={isFetching}
      onMonthChange={setViewedMonth}
      timezone="UTC"
    />
  );
};

export const ManualReviewPage = () => {
  const { location } = useParams<{ location: string }>();
  return (
    <ManualReviewPageShell key={location ?? ""} location={location ?? ""} />
  );
};

const ManualReviewPageShell = ({ location }: { location: string }) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const navigate = useNavigate();
  const {
    elapsedSeconds,
    isActive,
    getElapsedSeconds,
    reset,
    sessionStartedAt,
  } = useActivityTimer({
    storageKey: location ? `review-timer-${tenantName}-${location}` : undefined,
  });
  const [searchParams] = useSearchParams();

  const [selectedDate, setSelectedDate] = useQueryParamState<Dayjs, never>(
    "date",
    dayjsSerializer,
    dayjs.utc().subtract(1, "day")
  );

  const [showAllCranes, setShowAllCranes] = useLocalStorageState(
    "manualReviewShowAllCranes",
    true
  );

  const focusTimestamp = useMemo(
    () => parseFocusTimestampParam(searchParams.get("date")),
    [searchParams]
  );

  const reviewReasons = useMemo(() => {
    const parsed = reviewReasonListSerialiser.parse(
      searchParams.get("reviewReasonFilters") ?? ""
    );
    return parsed.valid ? requestedReviewReasons(parsed.value) : [];
  }, [searchParams]);

  const reviewDayOverviewQuery = useGetReviewDayOverviewQuery(
    {
      tenantName,
      date: selectedDate.format("YYYY-MM-DD"),
      location,
      reviewReasons: reviewReasons.length > 0 ? reviewReasons : undefined,
    },
    {
      skip: !location,
    }
  );

  const loadedReviewDayOverview = loadedEndpoint(reviewDayOverviewQuery);
  return (
    <HotkeyProvider>
      <VideoControlProvider>
        <VideoPlayerGroupProvider location={location}>
          <EventFilterProvider>
            <ExceptionDraftProvider location={location}>
              <AccountPageLayout sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton
                      onClick={() =>
                        void navigate(`/${tenantName}/tool/manual-review`)
                      }
                      size="small"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h1">
                      {t("manualReview")} - {location}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <EventFilterBar location={location} />
                    <ManualReviewLegend
                      date={selectedDate.format("YYYY-MM-DD")}
                      showAllCranes={showAllCranes}
                    />
                    <AssignedReviewIndicator location={location} />
                    <Button
                      variant="contained"
                      color="secondary"
                      component={Link}
                      to={`assign-reviewers?date=${dayjsSerializer.format(
                        selectedDate
                      )}`}
                    >
                      {t("assignReviewers")}
                    </Button>
                    <ActivityTimer
                      elapsedSeconds={elapsedSeconds}
                      isActive={isActive}
                    />
                    <ManualReviewDateStepper
                      location={location}
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                    />
                  </Stack>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <LoadedContent
                    data={loadedReviewDayOverview}
                    enableFullHeight
                    error={
                      <Alert severity="error">
                        {t("failedToLoadReviewData")}
                      </Alert>
                    }
                  >
                    {(dayOverview) => (
                      <ManualReviewPageContent
                        dayOverview={dayOverview}
                        selectedDate={selectedDate}
                        location={location}
                        focusTimestamp={focusTimestamp ?? null}
                        getElapsedSeconds={getElapsedSeconds}
                        resetTimer={reset}
                        sessionStartedAt={sessionStartedAt}
                        showAllCranes={showAllCranes}
                        setShowAllCranes={setShowAllCranes}
                      />
                    )}
                  </LoadedContent>
                </Box>
              </AccountPageLayout>
              <ExceptionDraftDialog />
            </ExceptionDraftProvider>
          </EventFilterProvider>
        </VideoPlayerGroupProvider>
      </VideoControlProvider>
    </HotkeyProvider>
  );
};

const PLAYLIST_DEBOUNCE_MS = 200;

const ManualReviewPageContent = ({
  dayOverview,
  selectedDate,
  location,
  focusTimestamp,
  getElapsedSeconds,
  resetTimer,
  sessionStartedAt,
  showAllCranes,
  setShowAllCranes,
}: {
  dayOverview: ReviewDayOverview;
  selectedDate: Dayjs;
  location: string;
  focusTimestamp: Dayjs | null;
  getElapsedSeconds: () => number;
  resetTimer: () => void;
  sessionStartedAt: number;
  showAllCranes: boolean;
  setShowAllCranes: (showAllCranes: boolean) => void;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const dayStart = useMemo(
    () => dayjs.utc(selectedDate.format("YYYY-MM-DD")).startOf("day"),
    [selectedDate]
  );
  const dayEnd = useMemo(
    () =>
      dayjs.utc(selectedDate.format("YYYY-MM-DD")).add(1, "day").startOf("day"),
    [selectedDate]
  );

  const isDirtyRef = useRef(false);

  const { viewingWindowStart, viewingWindowEnd, resetToOneHour, goToPeriod } =
    useManualReviewNavigation({
      dayStart,
      dayEnd,
      reviewPeriods: dayOverview?.review_periods ?? [],
      focusTimestamp,
      isDirtyRef,
    });

  const debouncedWindowStart = useDebouncedValue(
    viewingWindowStart,
    PLAYLIST_DEBOUNCE_MS
  );
  const debouncedWindowEnd = useDebouncedValue(
    viewingWindowEnd,
    PLAYLIST_DEBOUNCE_MS
  );

  // The page keeps showing the merge it has while the next one resolves, so it reads the
  // lagging response rather than gating on the resolved one as the focus surfaces do —
  // pulling the primary surface back to a spinner on every window step would cost more
  // than it buys, and nothing here is captured into a save.
  const resolveDefaultExclusions = useCallback(
    (report: TimelineExclusionReport) =>
      defaultExclusionPeriodIds({
        labellerTimelines: report.labeller_timelines,
        defaultTimelineExclusions: report.default_timeline_exclusions,
      }),
    []
  );
  const {
    latestResponse: eventsData,
    exclusions,
    isError: isEventsError,
    isFetching: isEventsQueryFetching,
  } = useReviewEventsWithExclusions({
    tenantName,
    location: location ?? "",
    windowStart: debouncedWindowStart,
    windowEnd: debouncedWindowEnd,
    resolveExclusions: resolveDefaultExclusions,
  });

  const { verifyPeriods, isVerifying } = useVerifyPeriods({
    tenantName,
    location,
    getElapsedSeconds,
    resetTimer,
    sessionStartedAt,
    reviewSlotRevisions: eventsData?.review_slot_revisions,
  });

  const combinedData: (ReviewDayOverview & EventsForReview) | undefined =
    useMemo(() => {
      if (!dayOverview || !eventsData) return undefined;
      return {
        exclude_material_from_labellers_by_default:
          eventsData.exclude_material_from_labellers_by_default ?? [],
        mass_outlier_bands: eventsData.mass_outlier_bands ?? [],
        heats: dayOverview.heats,
        review_periods: dayOverview.review_periods,
        ticks: dayOverview.ticks,
        loading_windows: dayOverview.loading_windows,
        review_events: eventsData.review_events,
        review_slot_revisions: eventsData.review_slot_revisions,
        labeller_timelines: eventsData.labeller_timelines,
        default_timeline_exclusions: eventsData.default_timeline_exclusions,
        exclusion_mode_applied: eventsData.exclusion_mode_applied ?? null,
        applied_exclusions: eventsData.applied_exclusions ?? null,
        targeted_review_periods: eventsData.targeted_review_periods,
        railcar_slot_materials: eventsData.railcar_slot_materials,
      };
    }, [dayOverview, eventsData]);

  const reviewPeriods = useMemo(
    () =>
      filterReviewPeriods(
        combinedData?.review_periods,
        viewingWindowStart,
        viewingWindowEnd
      ),
    [combinedData?.review_periods, viewingWindowStart, viewingWindowEnd]
  );

  const { extra_data: extraData } = useScrapEyeMetadata();
  const hasScaleWeights = extraData?.includes("mass_data") ?? false;

  // Show loading state while events data is loading
  if (!combinedData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEventsError) {
    return <Alert severity="error">{t("failedToLoadReviewData")}</Alert>;
  }

  return (
    <VerificationPreviewProvider>
      <ManualReviewEventProvider
        events={combinedData.review_events ?? []}
        reviewSlotRevisions={combinedData.review_slot_revisions}
        location={location}
        videoStart={debouncedWindowStart}
        windowStart={debouncedWindowStart}
        windowEnd={debouncedWindowEnd}
      >
        <TimelineExclusionProviders exclusions={exclusions}>
          <MassDataProvider
            windowStart={debouncedWindowStart}
            windowEnd={debouncedWindowEnd}
            location={location}
            enabled={hasScaleWeights}
          >
            <ManualReviewPageContentInner
              data={combinedData}
              dayOverview={dayOverview}
              location={location}
              reviewPeriods={reviewPeriods}
              viewingWindowStart={viewingWindowStart}
              viewingWindowEnd={viewingWindowEnd}
              debouncedWindowStart={debouncedWindowStart}
              debouncedWindowEnd={debouncedWindowEnd}
              resetToOneHour={resetToOneHour}
              goToPeriod={goToPeriod}
              dayStart={dayStart}
              dayEnd={dayEnd}
              isVerifying={isVerifying}
              verifyPeriods={verifyPeriods}
              isEventsFetching={isEventsQueryFetching}
              isDirtyRef={isDirtyRef}
              showAllCranes={showAllCranes}
              setShowAllCranes={setShowAllCranes}
            />
          </MassDataProvider>
        </TimelineExclusionProviders>
      </ManualReviewEventProvider>
    </VerificationPreviewProvider>
  );
};

const ManualReviewPageContentInner = ({
  data,
  dayOverview,
  location,
  reviewPeriods,
  viewingWindowStart,
  viewingWindowEnd,
  debouncedWindowStart,
  debouncedWindowEnd,
  resetToOneHour,
  goToPeriod,
  dayStart,
  dayEnd,
  isVerifying,
  verifyPeriods,
  isEventsFetching,
  isDirtyRef,
  showAllCranes,
  setShowAllCranes,
}: {
  data: EventsForReview & ReviewDayOverview;
  dayOverview: ReviewDayOverview;
  location: string;
  reviewPeriods: ReviewPeriodData[];
  viewingWindowStart: Dayjs;
  viewingWindowEnd: Dayjs;
  debouncedWindowStart: Dayjs;
  debouncedWindowEnd: Dayjs;
  resetToOneHour: (time: Dayjs) => void;
  goToPeriod: (period: ReviewPeriodData) => void;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  isVerifying: boolean;
  verifyPeriods: (
    periods: ReviewPeriodData[],
    allEvents: ScrapEyesEventWithReviewData[],
    originalEvents: ScrapEyesEventWithReviewData[],
    options?: {
      includeVerified?: boolean;
    }
  ) => Promise<void>;
  isEventsFetching: boolean;
  isDirtyRef: RefObject<boolean>;
  showAllCranes: boolean;
  setShowAllCranes: (showAllCranes: boolean) => void;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const [searchParams] = useSearchParams();

  const { data: exceptionalStatusData } =
    useListLocationExceptionalStatusesQuery({ tenantName });

  const { data: locationsLoadingTimeline } =
    useGetLocationsLoadingTimelineQuery(
      {
        tenantName,
        date: dayStart.format("YYYY-MM-DD"),
      },
      { skip: !location }
    );

  const { currentData: siteReviewPeriods, error: siteReviewPeriodsError } =
    useGetSiteReviewPeriodsQuery({
      tenantName,
      date: dayStart.format("YYYY-MM-DD"),
    });

  const showStack =
    siteReviewPeriodsError === undefined &&
    siteReviewPeriods !== undefined &&
    hasExpandableLane(siteReviewPeriods.locations, location);

  const hasOtherCranes = (siteReviewPeriods?.locations.length ?? 0) > 1;

  const siteLocations = siteReviewPeriods?.locations;
  const stackLocations = useMemo(() => {
    if (siteLocations === undefined) return [];
    if (showAllCranes) return siteLocations;
    return siteLocations.filter(
      ({ location_name: locationName }) => locationName === location
    );
  }, [siteLocations, showAllCranes, location]);

  const exceptionalStatusesByLocation = useMemo(
    () =>
      (exceptionalStatusData?.items ?? []).reduce((byLocation, item) => {
        const { location_name: locationName } = item;
        if (locationName === null) return byLocation;
        return byLocation.set(locationName, [
          ...(byLocation.get(locationName) ?? []),
          item,
        ]);
      }, new Map<string, LocationExceptionalStatusRead[]>()),
    [exceptionalStatusData]
  );

  const loadingIntervalsByLocation = useMemo(
    () =>
      locationsLoadingTimeline
        ? locationLoadingIntervals(locationsLoadingTimeline.buckets)
        : new Map<string, LoadingInterval[]>(),
    [locationsLoadingTimeline]
  );

  const openLocation = useCallback(
    (nextLocation: string, start: Dayjs) => {
      const params = new URLSearchParams(searchParams);
      params.set("date", start.format("YYYY-MM-DDTHH:mm:ss[Z]"));
      const url = `/${tenantName}/tool/manual-review/${encodeURIComponent(
        nextLocation
      )}?${params.toString()}`;
      window.open(url, "_blank");
    },
    [searchParams, tenantName]
  );

  const { hasUnsavedChanges } = useManualReviewEvents();

  useEffect(() => {
    isDirtyRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges, isDirtyRef]);

  // What this page currently keeps out of its merge, per labeller — its defaults plus the
  // reviewer's own toggles. The bring-in modal inherits these and maps them onto its own
  // window's labelling periods, so both surfaces exclude the same labellers rather than each
  // resolving defaults for a different window.
  const { excludedPeriodIds } = useExcludedPeriods();
  const { materialExcludedPeriodIds } = useMaterialExclusion();
  const { timingExcludedPeriodIds } = useTimingExclusion();
  const pageLabellerExclusions = useMemo(
    () =>
      labellerExclusionDecisions({
        labellerTimelines: data.labeller_timelines,
        excludedPeriodIds: {
          all: excludedPeriodIds,
          material: materialExcludedPeriodIds,
          timing: timingExcludedPeriodIds,
        },
      }),
    [
      data.labeller_timelines,
      excludedPeriodIds,
      materialExcludedPeriodIds,
      timingExcludedPeriodIds,
    ]
  );

  const railcarTicks = useRailcarTimelineTicks(dayStart, dayEnd);
  const matchesEventFilter = useEventFilterMatcher();
  const timelineTicks = useMemo(
    () => [
      ...thinTimelineTicks(
        (dayOverview.ticks ?? []).filter(matchesEventFilter)
      ),
      ...railcarTicks,
    ],
    [dayOverview.ticks, railcarTicks, matchesEventFilter]
  );

  return (
    <LinkedPeriodBringInProvider
      location={location}
      labellerExclusions={pageLabellerExclusions}
      omittedModelStatuses={[]}
    >
      <ManualReviewSurface
        location={location}
        reviewPeriods={reviewPeriods}
        allReviewPeriods={data.review_periods}
        viewingWindowStart={viewingWindowStart}
        viewingWindowEnd={viewingWindowEnd}
        debouncedWindowStart={debouncedWindowStart}
        debouncedWindowEnd={debouncedWindowEnd}
        isEventsFetching={isEventsFetching}
        isVerifying={isVerifying}
        verifyPeriods={verifyPeriods}
        goToPeriod={goToPeriod}
        labellerTimelines={data.labeller_timelines}
        railcarSlotMaterials={
          isEventsFetching ? undefined : data.railcar_slot_materials
        }
        reviewEvents={data.review_events}
        heats={dayOverview.heats}
        loadingWindows={dayOverview.loading_windows ?? []}
        dayStart={dayStart}
        dayEnd={dayEnd}
        navigationWindowMinutes={DEFAULT_WINDOW_MINUTES}
      >
        {showStack ? (
          <>
            {hasOtherCranes ? (
              <HotkeySafeCheckbox
                checked={showAllCranes}
                onChange={setShowAllCranes}
                label={t("showAllCranes")}
                sx={{ ml: 0, mb: 0.5 }}
              />
            ) : null}
            <LocationTimelineStack
              locations={stackLocations}
              expandedLocation={location}
              viewingWindowStart={viewingWindowStart}
              viewingWindowEnd={viewingWindowEnd}
              heats={dayOverview.heats}
              ticks={timelineTicks}
              expandedReviewPeriods={dayOverview.review_periods}
              exceptionalStatusesByLocation={exceptionalStatusesByLocation}
              loadingIntervalsByLocation={loadingIntervalsByLocation}
              onWindowChange={resetToOneHour}
              onOpenLocation={openLocation}
              dayStart={dayStart}
              dayEnd={dayEnd}
            />
          </>
        ) : (
          <DayTimeline
            viewingWindowStart={viewingWindowStart}
            viewingWindowEnd={viewingWindowEnd}
            heats={dayOverview.heats}
            ticks={timelineTicks}
            reviewPeriods={dayOverview.review_periods}
            exceptionalStatuses={
              exceptionalStatusesByLocation.get(location) ??
              EMPTY_EXCEPTIONAL_STATUSES
            }
            locationsLoadingTimeline={locationsLoadingTimeline}
            onWindowChange={resetToOneHour}
            dayStart={dayStart}
            dayEnd={dayEnd}
          />
        )}
      </ManualReviewSurface>
    </LinkedPeriodBringInProvider>
  );
};
